// Types communs pour l'application

export type ContractType = 'CDI' | 'CDD' | 'Freelance' | 'Stage' | 'Alternance';
export type PostTone = 'Formelle' | 'Décontractée' | 'Provocante' | 'Inspirante';
export type Tab = 'account' | 'posts' | 'write' | 'bank' | 'setup' | 'learn' | 'pimper';

// Types de rôles utilisateur
export type UserRole = 'trial' | 'starter' | 'pro' | 'entreprise' | 'admin' | 'member';

export interface User {
  id: string;
  email: string;
  name?: string;
  role: UserRole;
  createdAt: string;
  lastLogin?: string;
  remainingCredits: number;
  subscriptionEnd?: string;
  linkedInId?: string;
  linkedInToken?: string;
  linkedInTokenExpiry?: string;
  password: string;
  subscriptionAmount?: string;
  subscriptionDate?: string;
  nextBillingDate?: string;
}

// Configuration des plans
export interface PlanConfig {
  name: string;
  price: string;
  maxPosts: number | 'Illimité';
  maxSavedPosts: number | 'Illimité';
  maxCompanyValues: number;
  advancedFeatures: boolean;
  canExport: boolean;
  canAccessSetup: boolean;
  canUseLinkedIn: boolean;
  features: string[];
  stripePriceId: string;
  stripeProductId: string;
  users: number | 'Illimité';
}

// Limites et fonctionnalités par type de compte
export const USER_LIMITS: Record<UserRole, PlanConfig> = {
  trial: {
    name: 'Essai',
    price: '0',
    maxPosts: 5,
    maxSavedPosts: 2,
    maxCompanyValues: 3,
    advancedFeatures: false,
    canExport: true,
    canAccessSetup: false,
    canUseLinkedIn: false,
    features: [
      'Génération IA',
      'Export PDF',
      '5 offres gratuites',
      '1 utilisateur'
    ],
    stripePriceId: '',
    stripeProductId: '',
    users: 1
  },
  starter: {
    name: 'Starter',
    price: '9.90',
    maxPosts: 10,
    maxSavedPosts: 5,
    maxCompanyValues: 3,
    advancedFeatures: false,
    canExport: true,
    canAccessSetup: false,
    canUseLinkedIn: false,
    features: [
      'Génération IA',
      'Export PDF',
      '10 offres par mois',
      '1 utilisateur'
    ],
    stripePriceId: 'price_1OvCwbFXJGbxXPPPJGbxXPPP0000',
    stripeProductId: 'prod_Rxf6CnVpzNLwPE',
    users: 1
  },
  pro: {
    name: 'Pro',
    price: '19.90',
    maxPosts: 30,
    maxSavedPosts: 50,
    maxCompanyValues: 5,
    advancedFeatures: true,
    canExport: true,
    canAccessSetup: false,
    canUseLinkedIn: true,
    features: [
      'API LinkedIn',
      'Sauvegarde des offres',
      '30 offres par mois',
      '1 utilisateur'
    ],
    stripePriceId: 'price_1OvCwbFXJGbxXPPPJGbxXPPP0001',
    stripeProductId: 'prod_Rxf7sKfmhc4W7s',
    users: 1
  },
  entreprise: {
    name: 'Entreprise',
    price: '49.90',
    maxPosts: 'Illimité',
    maxSavedPosts: 'Illimité',
    maxCompanyValues: 'Illimité',
    advancedFeatures: true,
    canExport: true,
    canAccessSetup: true,
    canUseLinkedIn: true,
    features: [
      '3 utilisateurs',
      'Offres illimitées',
      'API LinkedIn',
      'Toutes les fonctionnalités'
    ],
    stripePriceId: 'price_1OvCwbFXJGbxXPPPJGbxXPPP0002',
    stripeProductId: 'prod_Rxf7dPnKnt9L74',
    users: 3
  },
  admin: {
    name: 'Admin',
    price: '0',
    maxPosts: 'Illimité',
    maxSavedPosts: 'Illimité',
    maxCompanyValues: 'Illimité',
    advancedFeatures: true,
    canExport: true,
    canAccessSetup: true,
    canUseLinkedIn: true,
    features: [
      'Accès complet',
      'Gestion des utilisateurs',
      'Configuration système'
    ],
    stripePriceId: '',
    stripeProductId: '',
    users: 'Illimité'
  },
  member: {
    name: 'Membre',
    price: '0',
    maxPosts: 0,
    maxSavedPosts: 0,
    maxCompanyValues: 0,
    advancedFeatures: false,
    canExport: false,
    canAccessSetup: false,
    canUseLinkedIn: false,
    features: [
      'Accès limité',
      'Lecture seule'
    ],
    stripePriceId: '',
    stripeProductId: '',
    users: 1
  }
};

// Nouvelles interfaces pour l'anonymisation
export interface AnonymizationRule {
  pattern: string;
  replacement: string;
}

export interface AnonymizationRules {
  companyDescription?: string;
  replacements?: AnonymizationRule[];
}

export interface JobPostFormData {
  companyName: string;
  position: string;
  location: string;
  experience: string;
  skills: string;
  contractType: ContractType;
  tone: PostTone;
  jobLink?: string;
  companyValues?: string[];
  companyWebsite?: string;
  isBlinded?: boolean;
  sensitiveInfo?: {
    address?: string;
    brands?: string[];
    products?: string[];
    [key: string]: any;
  };
}